// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Bills LIST page (Stage 6P.4).
//
// Server-paginated list at /erp/purchase/bills.
//
// Columns (brief):
//   Bill No (link) · Bill Date · Supplier Invoice No · Supplier Invoice Date
//   Supplier (+ GSTIN tag) · PO No (link to /erp/purchase/orders/:poId)
//   GRN No (link to /erp/purchase/grns/:grnId) · Matching Type
//   Status (Tag via statusColor) · Match Status (Tag via matchStatusColor)
//   Grand Total · Actions (⋯ menu: View / Match / Approve / Cancel).
//
// Toolbar:
//   Search (q over bill_no / supplier_invoice_no / supplier_name)
//   Status filter · Match status filter · Date range · "New Bill" CTA.
//
// Pagination: 25 / 50 / 100.
//
// ADDITIVE-ONLY: this page reads `purchaseBillApi` + the shared
// status / match-status / capability helpers (`statusLabel`,
// `statusColor`, `matchStatusLabel`, `matchStatusColor`,
// `canMatchPurchaseBill`, `canApprovePurchaseBill`,
// `canCancelPurchaseBill`, `canEditPurchaseBill`, `PB_STATUSES`,
// `PB_MATCH_STATUSES`) from `./purchaseBillHelpers`. The Bill
// detail / create / edit routes are owned by the Bill editor page
// — this list page only navigates and never mutates rows itself
// except for the match / approve / cancel / soft-delete endpoints
// shipped by the 6P.4 backend.
//
// Mirrors the compact enterprise AntD list pattern from
// GoodsReceiptsPage:
//   • slate-50 header strip + uppercase column titles
//   • 22 px row density (we reuse the .grn-* CSS scoped under
//     a dedicated .pb-shell wrapper class to keep DOM symmetry while
//     letting the shared styles win — see GoodsReceiptsPage.css for
//     the source styles).
//   • sticky toolbar
//   • ⋯ row-actions menu
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Dropdown, Input, Modal, Select,
  Space, Spin, Table, Tag, Tooltip, message
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, SearchOutlined, MoreOutlined,
  FileTextOutlined, ExclamationCircleOutlined,
  EyeOutlined, EditOutlined, StopOutlined,
  CheckCircleOutlined, LinkOutlined
} from '@ant-design/icons';

import { purchaseBillApi } from './purchaseBillApi';
import {
  PB_STATUSES,
  PB_MATCH_STATUSES,
  statusLabel,
  statusColor,
  matchStatusLabel,
  matchStatusColor,
  canEditPurchaseBill,
  canMatchPurchaseBill,
  canApprovePurchaseBill,
  canCancelPurchaseBill
} from './purchaseBillHelpers';
import useAuth from '../../../hooks/useAuth';
import './GoodsReceiptsPage.css';

const { RangePicker } = DatePicker;

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const DATE_FMT           = 'DD-MMM-YYYY';
const ISO_FMT            = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;

const STATUS_OPTIONS = PB_STATUSES.map((v) => ({
  value: v,
  label: statusLabel(v)
}));

const MATCH_STATUS_OPTIONS = PB_MATCH_STATUSES.map((v) => ({
  value: v,
  label: matchStatusLabel(v)
}));

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

// ₹ grouping is locale-aware (en-IN) so 12,34,567.89 is rendered
// the same way as elsewhere on the control plane. Falsy / non-finite
// inputs collapse to an em-dash so empty rows don't read as "₹0".
function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch {
    return n.toFixed(2);
  }
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// Matching type cell — backend sends 'TWO_WAY' / 'THREE_WAY'.
function matchingTypeLabel(value) {
  const key = String(value || '').trim().toUpperCase();
  if (key === 'TWO_WAY')   return '2-Way';
  if (key === 'THREE_WAY') return '3-Way';
  return '—';
}

// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function PurchaseBillsPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  // Wildcard '*' is the master role; explicit `erp.purchase.bills.*`
  // strings cover admin/manager/staff/viewer per userPermissions.js.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView    = hasPerm('erp.purchase.bills.view');
  const canCreate  = hasPerm('erp.purchase.bills.create');
  const canMatch   = hasPerm('erp.purchase.bills.match');
  const canApprove = hasPerm('erp.purchase.bills.approve');
  const canCancel  = hasPerm('erp.purchase.bills.cancel');
  const canUpdate  = hasPerm('erp.purchase.bills.update');

  // ── Toolbar state ────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [search,      setSearch]      = useState('');
  const [status,      setStatus]      = useState(null);
  const [matchStatus, setMatchStatus] = useState(null);
  const [dateRange,   setDateRange]   = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId,  setBusyId]  = useState(null);

  // ── Debounced search wiring ──────────────────────────────────
  // Keep the visible input snappy, but only re-fetch once the
  // operator pauses typing — same pattern as the GRN list.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = { page, pageSize };
      const needle = (search || '').trim();
      // Send under BOTH `q` (brief language) and `search` (purchase
      // module convention) — backend tolerates the unused one via
      // stripUnknown:true on its Joi validator.
      if (needle) {
        params.q      = needle;
        params.search = needle;
      }
      if (status)      params.status       = status;
      if (matchStatus) params.match_status = matchStatus;
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.fromDate = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.toDate   = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out = await purchaseBillApi.list(params);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : [];
      const count = Array.isArray(out) ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      setRows(list);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load purchase bills.'),
        key: 'pb-list'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, page, pageSize, search, status, matchStatus, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers — any filter change resets page to 1 ────
  const onStatus      = (v) => { setStatus(v || null);      setPage(1); };
  const onMatchStatus = (v) => { setMatchStatus(v || null); setPage(1); };
  const onDates       = (v) => { setDateRange(v || null);   setPage(1); };
  const onReset       = () => {
    setSearchInput('');
    setSearch('');
    setStatus(null);
    setMatchStatus(null);
    setDateRange(null);
    setPage(1);
  };

  // ── Row navigation ───────────────────────────────────────────
  const openView = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    navigate(`/erp/purchase/bills/${encodeURIComponent(id)}`);
  }, [navigate]);

  const openEdit = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    navigate(`/erp/purchase/bills/${encodeURIComponent(id)}?mode=edit`);
  }, [navigate]);

  const openSourcePo = useCallback((row) => {
    const poId = pickField(row, 'purchase_order_id', 'purchaseOrderId')
      || pickField(row, 'po_id', 'poId');
    if (!poId) return;
    navigate(`/erp/purchase/orders/${encodeURIComponent(poId)}`);
  }, [navigate]);

  const openSourceGrn = useCallback((row) => {
    const grnId = pickField(row, 'goods_receipt_id', 'goodsReceiptId')
      || pickField(row, 'grn_id', 'grnId');
    if (!grnId) return;
    navigate(`/erp/purchase/grns/${encodeURIComponent(grnId)}`);
  }, [navigate]);

  const openNew = useCallback(() => {
    navigate('/erp/purchase/bills/new');
  }, [navigate]);

  // ── Row actions — guarded by perms + status capability gates ─
  const runMatch = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    Modal.confirm({
      title: 'Run matching now?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <span style={{ fontSize: 12.5 }}>
          This will re-run PO/GRN reconciliation for bill{' '}
          <strong>{pickField(row, 'bill_no', 'billNo')}</strong>.
          Mismatches will move the bill to <em>Mismatch (Hold)</em>.
        </span>
      ),
      okText: 'Run matching',
      cancelText: 'Cancel',
      async onOk() {
        setBusyId(id);
        try {
          await purchaseBillApi.match(id);
          message.success('Matching complete.');
          await load();
        } catch (e) {
          message.error(errMessage(e, 'Failed to run matching.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [load]);

  const runApprove = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    Modal.confirm({
      title: 'Approve purchase bill?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <span style={{ fontSize: 12.5 }}>
          This will mark bill{' '}
          <strong>{pickField(row, 'bill_no', 'billNo')}</strong>{' '}
          as approved. Only matched bills can be approved.
        </span>
      ),
      okText: 'Approve',
      cancelText: 'Cancel',
      async onOk() {
        setBusyId(id);
        try {
          await purchaseBillApi.approve(id);
          message.success('Bill approved.');
          await load();
        } catch (e) {
          message.error(errMessage(e, 'Failed to approve bill.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [load]);

  const runCancel = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    let reason = '';
    Modal.confirm({
      title: 'Cancel purchase bill?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p style={{ marginBottom: 8, fontSize: 12.5 }}>
            This will mark bill{' '}
            <strong>{pickField(row, 'bill_no', 'billNo')}</strong>{' '}
            as cancelled. Provide a short reason (optional).
          </p>
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder="Reason (optional)"
            onChange={(e) => { reason = e.target.value; }}
          />
        </div>
      ),
      okText: 'Cancel Bill',
      okButtonProps: { danger: true },
      cancelText: 'Keep',
      async onOk() {
        setBusyId(id);
        try {
          await purchaseBillApi.cancel(id, { reason: reason || undefined });
          message.success('Bill cancelled.');
          await load();
        } catch (e) {
          message.error(errMessage(e, 'Failed to cancel bill.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [load]);

  // ── Row-action menu builder ──────────────────────────────────
  const buildRowMenu = useCallback((row) => {
    const items = [];
    items.push({
      key:   'view',
      icon:  <EyeOutlined />,
      label: 'View'
    });
    if (canUpdate && canEditPurchaseBill(row)) {
      items.push({
        key:   'edit',
        icon:  <EditOutlined />,
        label: 'Edit'
      });
    }
    if (canMatch && canMatchPurchaseBill(row)) {
      items.push({
        key:   'match',
        icon:  <LinkOutlined />,
        label: 'Match'
      });
    }
    if (canApprove && canApprovePurchaseBill(row)) {
      items.push({
        key:   'approve',
        icon:  <CheckCircleOutlined />,
        label: 'Approve'
      });
    }
    if (canCancel && canCancelPurchaseBill(row)) {
      if (items.length > 1) items.push({ type: 'divider' });
      items.push({
        key:   'cancel',
        icon:  <StopOutlined />,
        label: 'Cancel',
        danger: true
      });
    }
    return items;
  }, [canUpdate, canMatch, canApprove, canCancel]);

  const onRowMenu = useCallback((key, row) => {
    switch (key) {
      case 'view':    return openView(row);
      case 'edit':    return openEdit(row);
      case 'match':   return runMatch(row);
      case 'approve': return runApprove(row);
      case 'cancel':  return runCancel(row);
      default:        return undefined;
    }
  }, [openView, openEdit, runMatch, runApprove, runCancel]);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Bill No',
      dataIndex: 'billNo',
      width:     150,
      render: (_v, row) => {
        const billNo = pickField(row, 'bill_no', 'billNo') || '—';
        return (
          <button
            type="button"
            className="grn-link"
            onClick={() => openView(row)}
          >
            {billNo}
          </button>
        );
      }
    },
    {
      title:     'Bill Date',
      dataIndex: 'billDate',
      width:     120,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDate(pickField(row, 'bill_date', 'billDate'))}
        </span>
      )
    },
    {
      title:     'Supplier Invoice No',
      dataIndex: 'supplierInvoiceNo',
      width:     170,
      render: (_v, row) => {
        const ref = pickField(row, 'supplier_invoice_no', 'supplierInvoiceNo');
        if (!ref) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{ref}</span>;
      }
    },
    {
      title:     'Supplier Invoice Date',
      dataIndex: 'supplierInvoiceDate',
      width:     140,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDate(pickField(row, 'supplier_invoice_date', 'supplierInvoiceDate'))}
        </span>
      )
    },
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => {
        const name  = pickField(row, 'supplier_name',  'supplierName')  || '—';
        const gstin = pickField(row, 'supplier_gstin', 'supplierGstin');
        return (
          <div className="grn-supplier">
            <Tooltip title={name}>
              <span className="grn-supplier__name">{name}</span>
            </Tooltip>
            {gstin
              ? <Tag color="blue" className="grn-gstin-tag">{gstin}</Tag>
              : null}
          </div>
        );
      }
    },
    {
      title:     'PO No',
      dataIndex: 'poNumberSnapshot',
      width:     150,
      render: (_v, row) => {
        const poNo = pickField(row, 'po_number_snapshot', 'poNumberSnapshot')
          || pickField(row, 'purchase_order_number', 'purchaseOrderNumber');
        const poId = pickField(row, 'purchase_order_id', 'purchaseOrderId')
          || pickField(row, 'po_id', 'poId');
        if (!poNo && !poId) return <span className="grn-muted">—</span>;
        const label = poNo || poId;
        if (!poId) {
          return <span className="grn-mono">{label}</span>;
        }
        return (
          <button
            type="button"
            className="grn-link grn-link--secondary"
            onClick={() => openSourcePo(row)}
          >
            {label}
          </button>
        );
      }
    },
    {
      title:     'GRN No',
      dataIndex: 'grnNoSnapshot',
      width:     150,
      render: (_v, row) => {
        const grnNo = pickField(row, 'grn_no_snapshot', 'grnNoSnapshot')
          || pickField(row, 'goods_receipt_number', 'goodsReceiptNumber')
          || pickField(row, 'grn_no', 'grnNo');
        const grnId = pickField(row, 'goods_receipt_id', 'goodsReceiptId')
          || pickField(row, 'grn_id', 'grnId');
        if (!grnNo && !grnId) return <span className="grn-muted">—</span>;
        const label = grnNo || grnId;
        if (!grnId) {
          return <span className="grn-mono">{label}</span>;
        }
        return (
          <button
            type="button"
            className="grn-link grn-link--secondary"
            onClick={() => openSourceGrn(row)}
          >
            {label}
          </button>
        );
      }
    },
    {
      title:     'Matching',
      dataIndex: 'matchingType',
      width:     110,
      render: (_v, row) => {
        const v = pickField(row, 'matching_type', 'matchingType');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag className="grn-status-tag" color="geekblue">
            {matchingTypeLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Status',
      dataIndex: 'status',
      width:     150,
      render: (v) => {
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag color={statusColor(v)} className="grn-status-tag">
            {statusLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Match Status',
      dataIndex: 'matchStatus',
      width:     150,
      render: (_v, row) => {
        const v = pickField(row, 'match_status', 'matchStatus');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag color={matchStatusColor(v)} className="grn-status-tag">
            {matchStatusLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Grand Total',
      dataIndex: 'grandTotal',
      width:     130,
      align:     'right',
      render: (_v, row) => {
        const amt = pickField(row, 'grand_total', 'grandTotal');
        return (
          <span className="grn-mono grn-line-count">
            {fmtMoney(amt)}
          </span>
        );
      }
    },
    {
      title:     '',
      key:       '_actions',
      width:     56,
      fixed:     'right',
      align:     'center',
      render: (_v, row) => {
        const items = buildRowMenu(row);
        const id = pickField(row, 'id', 'id');
        return (
          <Dropdown
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                if (domEvent && domEvent.stopPropagation) domEvent.stopPropagation();
                onRowMenu(key, row);
              }
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              className="grn-actions-btn"
              icon={<MoreOutlined />}
              loading={busyId === id}
              onClick={(e) => e.stopPropagation()}
              aria-label="Row actions"
            />
          </Dropdown>
        );
      }
    }
  ]), [openView, openSourcePo, openSourceGrn, buildRowMenu, onRowMenu, busyId]);

  // ── Render ───────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view purchase bills."
          description="Ask your master account to grant erp.purchase.bills.view."
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><FileTextOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">Purchase Bills</span>
            <span className="grn-headstrip__subtitle">
              Capture supplier invoices, reconcile against POs and GRNs, and
              gate approval on match outcomes. Backend remains the authority
              for bill math, tax splits, and PO/GRN match deltas.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> total
          </span>
        </Space>
      </div>

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search bill no, supplier invoice, supplier…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={(e) => {
            // Flush debounce on Enter.
            setSearchInput(e.target.value);
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 300 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All statuses"
          value={status || undefined}
          onChange={onStatus}
          options={STATUS_OPTIONS}
          style={{ width: 180 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All match statuses"
          value={matchStatus || undefined}
          onChange={onMatchStatus}
          options={MATCH_STATUS_OPTIONS}
          style={{ width: 200 }}
        />
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['From', 'To']}
          style={{ width: 240 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
        <Tooltip
          title={canCreate
            ? 'Create a new purchase bill'
            : 'You do not have permission to create bills'}
        >
          <span>
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={openNew}
              disabled={!canCreate}
            >
              New Bill
            </Button>
          </span>
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) => pickField(r, 'id', 'id') || pickField(r, 'bill_no', 'billNo')}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            onRow={(row) => ({
              onDoubleClick: () => openView(row)
            })}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No purchase bills yet — click New Bill to start.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}
